import React from 'react';
import { Form, InputNumber, Table, Typography, Checkbox, Popover } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { frequencies } from './audiometryConfig';

const { Text } = Typography;

const parseFreqValue = (value) => {
  const rawValue = String(value ?? '');
  const num = parseInt(rawValue.replace('no escucha', ''), 10);
  return {
    num: isNaN(num) ? null : num,
    noHearing: rawValue.includes('no escucha'),
  };
};

const formatFreqValue = (num, noHearing) => {
  const hasNum = num != null && num !== '';
  if (!noHearing) return hasNum ? num : null;
  return `${hasNum ? num : ''} no escucha`.trim();
};

const AudiometryCell = ({ freq, record, disableForm, onValueChange, form }) => {
  const value = Form.useWatch([freq, record.key], form);
  const { num, noHearing } = parseFreqValue(value);

  const handleNumChange = (val) => {
    const newValue = formatFreqValue(val, noHearing);
    onValueChange(newValue, freq, record.key);
  };

  const handleNonHearing = (e) => {
    const checked = e.target.checked;
    const newValue = formatFreqValue(num, checked);
    
    form.setFieldsValue({
      [freq]: {
        ...form.getFieldValue(freq),
        [record.key]: newValue
      }
    });
    
    onValueChange(newValue, freq, record.key);
  };

  const popoverContent = (
    <div className="no-hearing-popover">
      <Checkbox 
        checked={noHearing} 
        onChange={handleNonHearing}
        disabled={disableForm}
      >
        No escucha
      </Checkbox>
    </div>
  );

  return (
    <div className="audiometry-cell-container">
      <div className="input-wrapper">
        <div className="ne-indicator">{noHearing ? 'NE' : ''}</div>
        <Form.Item 
          name={[freq, record.key]} 
          noStyle
          getValueProps={(frequencyValue) => ({ value: parseFreqValue(frequencyValue).num })}
          normalize={(frequencyValue, prev) => {
            const { noHearing: wasNoHearing } = parseFreqValue(prev);
            return formatFreqValue(frequencyValue, wasNoHearing);
          }}
        >
          <InputNumber
            disabled={disableForm}
            step={5}
            min={-10}
            max={120}
            size="small"
            controls={false}
            onFocus={(event) => event.target.select()}
            onChange={handleNumChange}
          />
        </Form.Item>
        <Popover 
          content={popoverContent} 
          trigger="click" 
          placement="bottom"
          overlayClassName="audiometry-popover"
        >
          <div className="toggle-icon">
            <CaretDownOutlined />
          </div>
        </Popover>
      </div>
    </div>
  );
};

const HorizontalAudiometryTable = ({
  form,
  sideType,
  onValueChange,
  disableForm,
}) => {
  const tableRows = [
    { key: `AC_${sideType}`, label: 'Aérea' },
    { key: `BC_${sideType}`, label: 'Ósea' },
    { key: `AC_${sideType}_masking`, label: 'Enmasc. A.' },
    { key: `BC_${sideType}_masking`, label: 'Enmasc. O.' },
  ];

  const columns = [
    {
      title: '',
      dataIndex: 'label',
      fixed: 'left',
      width: 60,
      render: (text) => <Text className="label-text">{text}</Text>
    },
    ...frequencies.map((freq) => ({
      title: <span className="header-text">{freq}</span>,
      dataIndex: freq,
      align: 'center',
      width: 60,
      render: (_, record) => (
        <AudiometryCell 
          freq={freq}
          record={record}
          disableForm={disableForm}
          onValueChange={onValueChange}
          form={form}
        />
      )
    }))
  ];

  return (
    <Table
      className="cmt-table compact-audiometry-table horizontal-audiometry-table"
      dataSource={tableRows}
      pagination={false}
      columns={columns}
      rowKey="key"
      bordered
      scroll={{ x: 'max-content' }}
    />
  );
};

export default HorizontalAudiometryTable;

